import { HsvColor } from 'solid-color';
import { Component, mergeProps, createSignal, createEffect } from 'solid-js';
import { calculateChange } from './color-utils';
import { clamp } from '@trimble/utilities';

export type SaturationProps = {
  hsv: HsvColor;
  onChange?(satVal: SaturationValue, event?: Event): void;
  width?: number;
  height?: number;
};

type Point2D = { x: number; y: number };
type SaturationValue = { s: number; v: number };

const saturationDivScale: number = 100;

export const Saturation: Component<SaturationProps> = (_props) => {
  const props = mergeProps({ styles: {} }, _props);
  let container: HTMLDivElement;

  const [cursorPos, setCursorPos] = createSignal<Point2D>({ x: 0, y: 0 });

  /**
   * Compute cursor pos inside the saturation container. Note (0, 0) is treated as bottom left corner
   * of saturation container but mouse coords are from the top left corner.
   * Also clamp within the dimensions of the container itself so the pointer does not exit it.
   */
  function getContainerLocation(
    container: HTMLDivElement,
    posX: number,
    posY: number
  ): Point2D {
    const rect = container.getBoundingClientRect();
    const x = clamp(posX - rect.left, 0, rect.width);
    const y = clamp(rect.height - (posY - rect.top), 0, rect.height);

    return { x, y };
  }

  createEffect(() => {
    const rect = container!.getBoundingClientRect();
    const x = clamp(props.hsv.s * rect.width, 0, rect.width);
    const y = clamp(props.hsv.v * rect.height, 0, rect.height);

    setCursorPos({ x, y });
  });

  function handleChange(event: MouseEvent | TouchEvent): void {
    if (props.onChange) {
      const { s, v } = calculateChange(event, container!);
      props.onChange({ s: s, v: v }, event as MouseEvent);
    }
    if (event instanceof MouseEvent) {
      setCursorPos(
        getContainerLocation(container!, event.clientX, event.clientY)
      );
    }
  }

  function handleMouseDown(event: MouseEvent): void {
    handleChange(event);

    document.addEventListener('mousemove', handleChange);
    document.addEventListener('mouseup', unbindEventListeners, {
      once: true,
    });
  }

  function unbindEventListeners(): void {
    document.removeEventListener('mousemove', handleChange);
  }

  return (
    <div
      style={{
        width: '100%',
        height: `${props.height!.toString()}px`,
        inset: '0px',
        background: `hsl(${props.hsv.h.toString()}, 100%, 50%)`,
      }}
      ref={container!}
      onMouseDown={handleMouseDown}
      onTouchMove={handleChange}
      onTouchStart={handleChange}
      data-testid="saturation-picker"
    >
      <style>
        {`
        .saturation-white {
          background: -webkit-linear-gradient(to right, #fff, rgba(255,255,255,0));
          background: linear-gradient(to right, #fff, rgba(255,255,255,0));
        }
        .saturation-black {
          background: -webkit-linear-gradient(to top, #000, rgba(0,0,0,0));
          background: linear-gradient(to top, #000, rgba(0,0,0,0));
        }`}
      </style>
      <div class="h-full w-full saturation-white">
        <div class="h-full w-full saturation-black" />
        <div
          style={{
            top: `${(
              -(props.hsv.v * saturationDivScale) + saturationDivScale
            ).toString()}%`,
            left: `${(props.hsv.s * saturationDivScale).toString()}%`,
            cursor: 'default',
          }}
        />
        <div
          class="relative border-2 border-white bg-gray-10 w-2 h-2"
          style={{
            left: `${cursorPos().x.toString()}px`,
            bottom: `${cursorPos().y.toString()}px`,
            transform: 'translate(-4px, -4px)',
          }}
          data-testid="saturation-cursor"
        />
      </div>
    </div>
  );
};

export default Saturation;
